import { Box, Button, IconButton, Typography } from '@mui/material';
import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { transportationInfos } from '../api';
import LoadingSpinner from '../components/LoadingSpinner';
import { TransportationTable } from '../components/TransportationTable';
import { AuthContext } from '../context/AuthContext';
import { ITransportationInfos } from '../utils/types';
import { AddTransportationRequisitionDialog } from '../components/HtmlContent/AddTransportationRequisitionsDialog';
import { AirplanemodeActiveTwoTone } from '@mui/icons-material';

type TransportInfoDetailsProps = {
  salesIndividualId: number;
};

export default function TransportInfoDetails({
  salesIndividualId,
}: TransportInfoDetailsProps) {
  const { token } = useContext(AuthContext);
  const { t } = useTranslation();
  const [, setError] = useState<string>();
  const [departureResults, setDepartureResults] = useState<
    ITransportationInfos[]
  >([]);
  const [returnsResults, setReturnsResults] = useState<ITransportationInfos[]>(
    [],
  );
  const [loading, setLoading] = useState<boolean>(true);
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [dialogDirection, setDialogDirection] = useState<
    'DEPARTURE' | 'RETURN'
  >('DEPARTURE');

  const canAddDeparture = departureResults.some(
    (d) => d.relatedTransportationAddable,
  );
  const canAddReturn = returnsResults.some(
    (r) => r.relatedTransportationAddable,
  );

  const handleDialogClose = () => {
    setOpenDialog(false);
  };

  const handleAdd = (direction: 'DEPARTURE' | 'RETURN') => {
    setDialogDirection(direction);
    setOpenDialog(true);
  };

  const handleSaveTransportation = async (isSuccess: boolean) => {
    setOpenDialog(false);
    if (isSuccess) {
      await fetchTransportationInfos(salesIndividualId);
    }
  };

  useEffect(() => {
    fetchTransportationInfos(salesIndividualId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [salesIndividualId]);

  const fetchTransportationInfos = async (id: number) => {
    try {
      const res = await transportationInfos(id, token);
      const data = (await res.json()) as ITransportationInfos[];
      const departure = data.filter((d) => d.direction === 'DEPARTURE');
      const returns = data.filter((d) => d.direction === 'RETURN');
      const sortedDeparture = departure.sort(function (a, b) {
        return a.departureDate.localeCompare(b.departureDate);
      });
      const sortedReturns = returns.sort(function (a, b) {
        return a.arrivalDate.localeCompare(b.arrivalDate);
      });
      if (res?.status === 200) {
        setDepartureResults(sortedDeparture);
        setReturnsResults(sortedReturns);
      } else {
        throw data;
      }
    } catch (e: any) {
      setError(e.detail || t('loginPage.registerErrorMessage'));
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Typography variant="h6">
          {t('transportInfoDetails.directionDeparture')}
        </Typography>
        {canAddDeparture && (
          <Button variant={'text'} onClick={() => handleAdd('DEPARTURE')}>
            {t('transportInfoDetails.addDomesticFlight')}
          </Button>
        )}
      </Box>

      <TransportationTable
        rows={departureResults.map((dr) => ({
          company: dr.transportationCompanyName,
          companyLogo: dr.transportationFullPathCompanyLogo,
          flightNumber: dr.flightNumber,
          departureDate: dr.departureDate,
          arrivalDate: dr.arrivalDate,
          departure: dr.departure,
          arrival: dr.arrival,
          arrivalTime: dr.arrivalTime,
          departureTime: dr.departureTime,
          durationInMinutes: dr.durationInMinutes,
        }))}
      />

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Typography variant="h6" sx={{ mt: 4 }}>
          {t('transportInfoDetails.directionReturn')}
        </Typography>
        {canAddReturn && (
          <Button variant={'text'} onClick={() => handleAdd('RETURN')}>
            {t('transportInfoDetails.addDomesticFlight')}
          </Button>
        )}
      </Box>
      <TransportationTable
        rows={returnsResults.map((rr) => ({
          company: rr.transportationCompanyName,
          companyLogo: rr.transportationFullPathCompanyLogo,
          flightNumber: rr.flightNumber,
          departureDate: rr.departureDate,
          arrivalDate: rr.arrivalDate,
          direction: rr.direction,
          departure: rr.departure,
          arrival: rr.arrival,
          transportationType: rr.transportationType,
          arrivalTime: rr.arrivalTime,
          departureTime: rr.departureTime,
          durationInMinutes: rr.durationInMinutes,
        }))}
      />
      <AddTransportationRequisitionDialog
        open={openDialog}
        onClose={handleDialogClose}
        onSave={handleSaveTransportation}
        salesIndividualId={salesIndividualId}
        direction={dialogDirection}
      />
    </Box>
  );
}
